import React, { useState } from 'react';
import './PermissionRequest.css';

export default function PermissionRequest({ onGranted, onDenied }) {
  const [status, setStatus] = useState('idle'); // idle, requesting, granted, denied, error
  const [error, setError] = useState(null);

  const requestPermissions = async () => {
    setStatus('requesting');
    setError(null);

    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errorMsg = 'Your browser does not support camera/microphone access. Please use a modern browser like Chrome, Firefox, or Edge.';
      setError(errorMsg);
      setStatus('denied');
      /* if (onDenied) {
        setTimeout(() => onDenied(new Error(errorMsg)), 1000);
      } */
      return;
    }

    try {
      // Request both camera and microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
        audio: true
      });

      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop());

      setStatus('granted');
      setTimeout(() => {
        if (onGranted) onGranted();
      }, 500);
    } catch (err) {
      console.error('Permission error:', err);
      let errorMessage = 'Permission denied';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera and microphone access was denied. Please allow access in your browser settings and try again.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'No camera or microphone found. Please connect a camera and microphone and try again.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Camera or microphone is already in use by another application. Please close other applications and try again.';
      } else {
        errorMessage = err.message || 'Failed to access camera and microphone.';
      }
      
      setError(errorMessage);
      setStatus('denied');
      
      /* if (onDenied) {
        setTimeout(() => onDenied(err), 1000);
      } */
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'requesting':
        return 'Requesting permissions...';
      case 'granted':
        return 'Permissions granted! ✓';
      case 'denied':
        return 'Permissions denied. Please allow camera and microphone access.';
      default:
        return null;
    }
  };

  return (
    <div className="permission-overlay">
      <div className="permission-modal">
        <div className="permission-icon">
          {status === 'granted' ? (
            <span className="icon-success">✓</span>
          ) : (
            <span className="icon-camera">📷</span>
          )}
        </div>
        
        <h2>Camera & Microphone Access Required</h2>
        
        <div className="permission-content">
          <p>
            To ensure exam integrity, we need access to your camera and microphone.
            This allows us to:
          </p>
          <ul>
            <li>Monitor your presence during the exam</li>
            <li>Detect any suspicious activity</li>
            <li>Ensure a fair testing environment</li>
          </ul>
          
          <div className="permission-note">
            <strong>Note:</strong> Your video and audio are only used for proctoring purposes 
            and are not stored permanently.
          </div>
        </div>

        {error && (
          <div className="permission-error">
            <strong>Error:</strong> {error}
            <br />
            <small>
              Please check your browser settings and allow camera/microphone access, 
              then refresh the page.
            </small>
          </div>
        )}

        <div className="permission-status">
          {getStatusMessage()}
        </div>

        <div className="permission-actions">
          {status === 'idle' && (
            <button 
              className="btn-primary" 
              onClick={requestPermissions}
            >
              Allow Camera & Microphone
            </button>
          )}
          {status === 'requesting' && (
            <button className="btn-primary" disabled>
              <span className="spinner"></span> Requesting...
            </button>
          )}
          {status === 'granted' && (
            <button className="btn-success" disabled>
              ✓ Ready to Continue
            </button>
          )}
          {status === 'denied' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', marginTop: '20px' }}>
              <button 
                className="btn-primary" 
                onClick={requestPermissions}
                style={{
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                Try Again
              </button>
              <div style={{ textAlign: 'center', margin: '10px 0', color: '#666' }}>— OR —</div>
              <button 
                className="btn-secondary" 
                onClick={() => onGranted && onGranted(true)}
                style={{ 
                  backgroundColor: '#4a5568', 
                  color: 'white', 
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Proceed without Camera & Mic
              </button>
              <p style={{ fontSize: '12px', color: '#718096', textAlign: 'center', marginTop: '8px' }}>
                Note: Performance and security monitoring will still be active.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

